import { Link } from "react-router-dom";
import { X, Plus, Minus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";
import { useCart } from "../../context/CartContext";
import { formatPrice } from "../../utils/formatPrice";
import { mediaUrl } from "../../services/api";

export default function CartDrawer() {
  const {
    isOpen, closeCart, items, updateQuantity, removeFromCart,
    subtotal, deliveryFee, discount, total, itemCount, coupon,
  } = useCart();

  return (
    <>
      <div
        className="fixed inset-0 z-[70] bg-black/40 backdrop-blur-sm transition-opacity"
        style={{ opacity: isOpen ? 1 : 0, pointerEvents: isOpen ? "auto" : "none" }}
        onClick={closeCart}
      />
      <aside
        className="fixed top-0 right-0 z-[80] h-full w-full sm:w-[450px] bg-white flex flex-col shadow-2xl border-l border-[#EAE8E4]"
        style={{
          transform: isOpen ? "translateX(0)" : "translateX(100%)",
          transition: "transform 360ms cubic-bezier(0.16, 1, 0.3, 1)"
        }}
        data-testid="cart-drawer"
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#EAE8E4]">
          <h2 className="font-display text-2xl text-[#111111]">
            Votre Rituel <span className="text-[#888888] text-sm font-body font-normal">({itemCount} soin{itemCount > 1 ? "s" : ""})</span>
          </h2>
          <button onClick={closeCart} aria-label="Fermer" data-testid="cart-close" className="p-2 text-[#111111] hover:opacity-60">
            <X size={20} />
          </button>
        </div>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-8 gap-4">
            <div className="w-16 h-16 rounded-full bg-[#F8F8F6] border border-[#EAE8E4] grid place-items-center text-[#111111]">
              <ShoppingBag size={24} />
            </div>
            <p className="font-display text-2xl text-[#111111]">Votre rituel est vide</p>
            <p className="text-[#666666] text-xs max-w-xs leading-relaxed">
              Découvrez nos sérums concentrés pour composer votre protocole de soin The Queen.
            </p>
            <Link to="/shop" onClick={closeCart} className="btn-queen-primary mt-3 text-xs uppercase tracking-wider">
              Découvrir les Sérums
            </Link>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {items.map((item) => (
                <div key={item.key} className="flex gap-4 p-3 bg-[#F8F8F6] rounded-2xl border border-[#EAE8E4]" data-testid={`cart-item-${item.slug}`}>
                  <Link to={`/product/${item.slug}`} onClick={closeCart} className="shrink-0 rounded-xl overflow-hidden bg-white w-20 h-24 p-1">
                    <img src={mediaUrl(item.image)} alt={item.name} className="w-full h-full object-contain mix-blend-multiply" />
                  </Link>
                  <div className="flex-1 min-w-0 flex flex-col justify-between">
                    <div>
                      <h3 className="font-display text-base text-[#111111] leading-tight line-clamp-1">{item.name}</h3>
                      <p className="text-[#111111] font-medium text-sm mt-1">{formatPrice(item.price)}</p>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center border border-[#EAE8E4] rounded-full bg-white">
                        <button onClick={() => updateQuantity(item.key, item.quantity - 1)} className="w-7 h-7 grid place-items-center text-[#666666] hover:text-[#111111]" aria-label="Diminuer">
                          <Minus size={12} />
                        </button>
                        <span className="w-6 text-center text-xs font-medium" data-testid={`cart-qty-${item.slug}`}>{item.quantity}</span>
                        <button onClick={() => updateQuantity(item.key, item.quantity + 1)} className="w-7 h-7 grid place-items-center text-[#666666] hover:text-[#111111]" aria-label="Augmenter">
                          <Plus size={12} />
                        </button>
                      </div>
                      <button onClick={() => removeFromCart(item.key)} className="text-[#888888] hover:text-red-600 transition-colors p-1" aria-label="Supprimer" data-testid={`cart-remove-${item.slug}`}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-[#EAE8E4] px-6 py-5 space-y-3 bg-white">
              <div className="flex justify-between text-xs font-body text-[#666666]">
                <span>Sous-total</span><span className="text-[#111111] font-medium">{formatPrice(subtotal)}</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between text-xs font-body text-emerald-700">
                  <span>Privilège {coupon?.code ? `(${coupon.code})` : ""}</span><span>-{formatPrice(discount)}</span>
                </div>
              )}
              <div className="flex justify-between text-xs font-body text-[#666666]">
                <span>Livraison</span>
                <span className="text-[#111111]">
                  {deliveryFee === 0 ? "Offerte" : deliveryFee === null ? "Calculée à l'étape suivante" : formatPrice(deliveryFee)}
                </span>
              </div>
              <div className="flex justify-between items-baseline pt-3 border-t border-[#EAE8E4]">
                <span className="font-display text-xl text-[#111111]">Total estimé</span>
                <span className="font-body text-2xl font-semibold text-[#111111]" data-testid="cart-total">
                  {total === null ? `${formatPrice(subtotal - discount)}` : formatPrice(total)}
                </span>
              </div>
              <Link to="/checkout" onClick={closeCart} className="btn-queen-primary w-full h-12 text-xs uppercase tracking-wider" data-testid="cart-checkout-button">
                Commander · Paiement à la réception <ArrowRight size={15} />
              </Link>
            </div>
          </>
        )}
      </aside>
    </>
  );
}
